import React, { useState } from 'react';
import { Banknote, Search, FileText, Handshake, FileCheck, Cpu, Building, Zap, Package, ArrowRight, Globe, Receipt, ChevronDown, ChevronUp, Landmark, TrendingUp  } from 'lucide-react';

// Import gotowych komponentów
import Stats from '../Stats';
import Trust from '../Trust';
import Newsletter from '../Newsletter';
import ContactForm from '../ContactForm';

// Dodanie importu czcionki Inter
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);


const PozyckiUnijne = () => {
  const [activeFaq, setActiveFaq] = useState(null);

  const handleContactClick = () => {
    const contactSection = document.querySelector('#kontakt');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "Czym pożyczki unijne różnią się od tradycyjnych kredytów?",
      answer: "Pożyczki unijne często charakteryzują się niższym oprocentowaniem, dłuższym okresem spłaty, możliwością karencji w spłacie kapitału oraz niższymi (lub brakującymi) wymaganiami dotyczącymi zabezpieczeń. Są one udzielane ze środków funduszy strukturalnych UE za pośrednictwem banków lub innych instytucji finansowych."
    },
    {
      question: "Kto może ubiegać się o pożyczkę unijną?",
      answer: "Pożyczki unijne są skierowane głównie do sektora MŚP (małych i średnich przedsiębiorstw), startupów oraz firm realizujących projekty innowacyjne, proekologiczne lub w określonych, priorytetowych branżach. Kryteria są różne dla poszczególnych programów."
    },
    {
      question: "Czy do pożyczki unijnej potrzebny jest biznes plan?",
      answer: "Tak, w większości przypadków wymagane jest przedstawienie solidnego biznes planu lub studium wykonalności, które uzasadnia celowość i opłacalność inwestycji. Pomożemy Ci przygotować taki dokument."
    }
  ];

  const SectionTitle = ({ children, className = '' }) => (
    <h2 
      className={`text-3xl lg:text-5xl text-white text-center uppercase ${className}`}
      style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
    >
      {children}
    </h2>
  );

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
    <section className="relative pt-28 sm:pt-32 pb-16 lg:pt-48 lg:pb-32 overflow-hidden bg-black">
        <div className="absolute inset-0 opacity-10">
          {/* Dekoracyjne, animowane ikony w tle */}
          <div className="absolute top-1/4 left-[15%] animate-float">
            <Banknote className="w-24 h-24 text-violet-600/40" />
          </div>
          <div className="absolute bottom-1/4 right-[15%] animate-float animation-delay-2000">
            <Landmark className="w-32 h-32 text-violet-600/40" />
          </div>
          <div className="absolute top-20 right-[40%] animate-float animation-delay-1000">
            <TrendingUp className="w-20 h-20 text-violet-600/40" />
          </div>
        </div>

        <div className="relative z-10 mx-auto text-center px-6">

            <h1 
              className="text-3xl sm:text-4xl md:text-6xl text-white mb-6 uppercase leading-tight"
              style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}
            >
              Pożyczki Unijne
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-6 sm:mb-8 font-semibold max-w-3xl mx-auto">
              Zdobądź preferencyjne finansowanie na rozwój i inwestycje w Twojej firmie z naszą pomocą.
            </p>
            <p className="text-base sm:text-lg text-gray-300 mb-8 sm:mb-10 max-w-3xl mx-auto">
              Szukasz alternatywnych źródeł finansowania? Odkryj potencjał pożyczek unijnych! Pomożemy Ci uzyskać dostęp do środków z niższym oprocentowaniem i na elastycznych warunkach, które realnie wesprą rozwój Twojej firmy.
            </p>
            <div className="flex justify-center">
                <button 
                    onClick={handleContactClick} 
                    className="group flex items-center justify-center gap-2 px-6 sm:px-8 py-3.5 sm:py-4 bg-gradient-to-r from-violet-600 to-violet-700 text-white font-semibold rounded-full transition-all duration-300 hover:from-violet-700 hover:to-violet-800 hover:scale-105 text-center"
                >
                    <span>Sprawdź warunki finansowania</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
            </div>
        </div>
        <style>{`
          @keyframes float {
              0%, 100% { transform: translateY(0px) rotate(0deg); }
              50% { transform: translateY(-20px) rotate(5deg); }
          }
          .animate-float {
              animation: float 8s ease-in-out infinite;
          }
          .animation-delay-1000 { animation-delay: 1s; }
          .animation-delay-2000 { animation-delay: 2s; }
        `}</style>
      </section>

      {/* Main Content Section */}
      <section className="py-16 md:py-24 relative bg-black">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTitle className="mb-6">Dostęp do preferencyjnych pożyczek</SectionTitle>
          <p className="text-lg text-gray-300 text-center mb-16 max-w-4xl mx-auto">
            Pożyczki unijne to atrakcyjna forma finansowania, oferująca niższe oprocentowanie i elastyczne warunki spłaty. Są doskonałą alternatywą dla tradycyjnych kredytów, szczególnie dla MŚP i startupów.
          </p>
          <div className="mb-16">
            <h3 className="text-2xl text-white font-bold mb-10 text-center uppercase" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
              Jak pomagamy w pozyskaniu pożyczek?
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Identyfikacja programów", description: "Wyszukujemy dostępne instrumenty finansowe w ramach programów unijnych.", icon: <Search className="w-7 h-7" /> },
                { title: "Analiza zdolności", description: "Pomagamy ocenić zdolność firmy do obsługi pożyczki i przygotować dokumenty finansowe.", icon: <FileCheck className="w-7 h-7" /> },
                { title: "Przygotowanie wniosku", description: "Sporządzamy kompleksowy wniosek o pożyczkę, dbając o jego poprawność formalną i merytoryczną.", icon: <FileText className="w-7 h-7" /> },
                { title: "Wsparcie w negocjacjach", description: "Reprezentujemy klientów w kontaktach z instytucjami finansowymi.", icon: <Handshake className="w-7 h-7" /> },
                { title: "Doradztwo w zakresie warunków", description: "Wyjaśniamy zawiłości umów pożyczkowych i warunków ich spłaty.", icon: <FileCheck className="w-7 h-7" /> },
                { title: "Pomoc w rozliczeniu pożyczki", description: "Zapewniamy wsparcie w prawidłowym rozliczeniu środków z pożyczki zgodnie z umową.", icon: <Receipt className="w-7 h-7" /> },
              ].map((service) => (
                <div key={service.title} className="backdrop-blur-md bg-white/5 rounded-xl p-6 border border-white/10 hover:border-violet-600/50 transition-all duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-full flex items-center justify-center flex-shrink-0 text-violet-600">{service.icon}</div>
                    <div><h4 className="text-lg text-white font-bold mb-2">{service.title}</h4><p className="text-gray-400 text-sm">{service.description}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Investment Goals Section */}
      <section className="py-16 md:py-24 relative bg-black">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <SectionTitle className="mb-12">Finansowanie na szeroki zakres celów</SectionTitle>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "Projekty Badawczo-Rozwojowe", description: "Wprowadzanie innowacji produktowych i procesowych.", icon: <Cpu className="w-8 h-8" /> },
              { title: "Inwestycje w Nowe Technologie", description: "Zakup nowoczesnych maszyn, urządzeń, oprogramowania.", icon: <Cpu className="w-8 h-8" /> },
              { title: "Rozwój Infrastruktury", description: "Budowa lub modernizacja obiektów produkcyjnych.", icon: <Building className="w-8 h-8" /> },
              { title: "Efektywność Energetyczna", description: "Wdrożenie rozwiązań proekologicznych i OZE.", icon: <Zap className="w-8 h-8" /> },
              { title: "Finansowanie Kapitału Obrotowego", description: "Bieżące finansowanie działalności (w wybranych programach).", icon: <Package className="w-8 h-8" /> },
              { title: "Ekspansja na rynki zagraniczne", description: "Wsparcie w internacjonalizacji działalności firmy.", icon: <Globe className="w-8 h-8" /> },
            ].map((goal) => (
              <div key={goal.title} className="backdrop-blur-md bg-white/5 rounded-2xl p-5 sm:p-8 border border-white/10 text-center hover:border-violet-600/50 transition-all duration-300">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-violet-600/20 to-violet-700/20 rounded-2xl mb-4 text-violet-500">{goal.icon}</div>
                <h3 className="text-xl font-bold text-white mb-3">{goal.title}</h3>
                <p className="text-gray-400">{goal.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Stats />
      <Trust />
      <Newsletter />
      
      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <SectionTitle className="mb-12">Często zadawane pytania</SectionTitle>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="backdrop-blur-md bg-white/5 rounded-xl border border-white/10 overflow-hidden transition-all duration-300 hover:border-violet-600/30">
                <button 
                  onClick={() => toggleFaq(index)} 
                  className="w-full px-6 py-4 text-left flex justify-between items-center group"
                >
                  <span className="font-semibold text-lg text-white pr-4">{faq.question}</span>
                  {activeFaq === index ? 
                    <ChevronUp className="w-5 h-5 text-violet-600 flex-shrink-0" /> :
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-violet-600 transition-colors flex-shrink-0" />
                  }
                </button>
                <div className={`transition-all duration-300 ${activeFaq === index ? 'max-h-96' : 'max-h-0'}`}>
                  <div className="overflow-hidden px-6 pb-4">
                    <p className="text-gray-300 pt-2">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      <div id="kontakt"><ContactForm /></div>

      <style>{`
        .flow-animation { position: relative; width: 100%; height: 100%; }
        .flow-node { 
            position: absolute; 
            width: 4rem; height: 4rem; 
            border-radius: 9999px; display: flex; align-items: center; justify-content: center;
            background: radial-gradient(circle, rgba(249, 115, 22, 0.15) 0%, rgba(249, 115, 22, 0) 70%);
            border: 1px solid rgba(249, 115, 22, 0.2);
            color: #fb923c; /* text-violet-500 */
        }
        .flow-node.source { top: 50%; left: 0; transform: translateY(-50%); }
        .flow-node.target.t1 { top: 0; right: 0; }
        .flow-node.target.t2 { top: 50%; right: 0; transform: translateY(-50%); }
        .flow-node.target.t3 { bottom: 0; right: 0; }

        .flow-particle {
            position: absolute; top: 50%; left: 0;
            width: 0.5rem; height: 0.5rem;
            background-color: #f97316; /* violet-600 */
            border-radius: 9999px;
            box-shadow: 0 0 10px #f97316, 0 0 20px #f97316;
            animation: flow-path 4s infinite linear;
            transform-origin: 50% 50%;
        }
        .flow-particle.p2 { animation-delay: 1.3s; }
        .flow-particle.p3 { animation-delay: 2.6s; }
        
        @keyframes flow-path {
            0% { transform: translate(2rem, -1.5rem) scale(0.5); opacity: 0; }
            20% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translate(calc(100% - 6rem), -12rem) scale(1); opacity: 0; }
        }
        .flow-particle.p2 { animation-name: flow-path2; }
        @keyframes flow-path2 {
            0% { transform: translate(2rem, -1.5rem) scale(0.5); opacity: 0; }
            20% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translate(calc(100% - 6rem), 0rem) scale(1); opacity: 0; }
        }
        .flow-particle.p3 { animation-name: flow-path3; }
        @keyframes flow-path3 {
            0% { transform: translate(2rem, -1.5rem) scale(0.5); opacity: 0; }
            20% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translate(calc(100% - 6rem), 12rem) scale(1); opacity: 0; }
        }
        .max-h-96 { max-height: 24rem; }
      `}</style>
    </div>
  );
};

export default PozyckiUnijne;